/* REACT */
import React, { memo } from 'react'
import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
} from 'react'
import { connect } from 'react-redux'
import cloneDeep from 'lodash.clonedeep'

/* COMPONENTS */
import MessageComponent from './MessageComponent'
import Typing from './Typing'
import { Image } from '../../../../components/ui'
import FeedbackModal from '../../../../components/modals/feedback/FeedbackModal'

/* STORE */
import {
  sendMessageToMaya,
  setRegenerateResponse,
  requestPlan,
  setIsGridMode,
  updateLastActiveTime,
  setShowFeedbackModal,
} from '../../../../store/maya/actions'
import { getGrids } from '../../../../store/maya/selectors'

/* STYLES */
import {
  MayaInputContainer,
  ChatSectionWrapper,
  ChatSectionScrollable,
  MayaMessageInput,
  MayaMessageInputSubmitButton,
  MayaQuickRepliesConatiner,
  MayaQuickRepliesBullet,
  RegenerateResponse,
  MayaInputContainerWrapper,
  TypingContainerInInput,
  TypingDot,
  MayaInputBox,
  ChatSectionCta,
  ChatSectionCtaLink,
  MayaEmptyMarginContainer,
  ChatSectionCtaWrapper,
  PoweredByMayaWrapper,
  PoweredByMayaLink,
  PoweredByMayaText,
  ReadOnlyDisclaimerWrapper,
} from './style.js'

/* UTILS/CONSTANTS */
import { CLOUDFRONT_BASE_URL } from '../../../../utils/constants'
import { Analytics, getTranslations } from '../../../../utils'

/* ASSETS */

let lastUpdateTimeCall = 0

const arePropsEqual = (prevProps, nextProps) => {
  // Compare only the props that would trigger a meaningful re-render
  return (
    prevProps.messages === nextProps.messages &&
    prevProps.isMayaResponding === nextProps.isMayaResponding &&
    prevProps.quickReplies === nextProps.quickReplies &&
    prevProps.regenerateResponse === nextProps.regenerateResponse &&
    prevProps.showFeedbackModal === nextProps.showFeedbackModal &&
    prevProps.isSocketConnectionEstablished ===
      nextProps.isSocketConnectionEstablished
  )
}

const ChatSection = ({
  isPlanRequested,
  isChatReady,
  isLessThanLaptop,
  isHorizontal,
  readOnlyMode,
  slug,
  token,
  messages,
  isMayaResponding,
  quickReplies,
  regenerateResponse,
  setRegenerateResponse,
  defaultMessages,
  language,
  intent,
  grids,
  setTravelPlanView = () => {},
  organizationSlug,
  setIsGridMode,
  isSocketConnectionEstablished,
  organizationDetails,
  showFeedbackModal,
  updateLastActiveTime,
  setShowFeedbackModal,
}) => {
  const SubmitText = `${CLOUDFRONT_BASE_URL}/assets/${organizationSlug}/images/MayaSendTextButton.png`

  const [prompt, setPrompt] = useState('')
  const [mainDivHeight, setMainDivHeight] = useState(0)
  const [currentMessagesLength, setCurrentMessagesLength] = useState(0)
  const stopScrollDown = useRef(false)
  const assistantMsgTop = useRef(null)
  const isTyping = useRef(false)

  const executeScroll = useCallback(() => {
    if (!defaultMessages?.length) return // If there are no default messages, don't scroll, because it will scroll to the bottom of the page
    if (messages?.length <= defaultMessages?.length) return // If the number of messages is less or equal to the number of default messages, don't scroll, as it's default chat

    if (isTyping.current) return

    const element = document.getElementById('chat-section-scrollable')
    if (element) {
      element.scrollTop = element.scrollHeight
    }
  }, [defaultMessages])

  // Add effect to scroll when feedback modal appears
  useEffect(() => {
    if (showFeedbackModal) {
      // Allow auto scrolling when feedback modal appears
      stopScrollDown.current = false
      executeScroll()
    }
  }, [showFeedbackModal, executeScroll])

  useLayoutEffect(() => {
    // MAKE SURE THAT CHAT SECTION IS SCROLLED TO BOTTOM WHEN HEIGHT IS SETTING FOR MAYA-B2B-FULL-PAGE DIV
    const mainDivObserver = new ResizeObserver(entries => {
      entries.forEach(entry => {
        if (entry.contentRect.height !== mainDivHeight) {
          setMainDivHeight(entry.contentRect.height)
          executeScroll()
        }
      })
    })

    const someEl = document.querySelector(
      '#maya-b2b-full-page[data-widget-mode="full-page"], #maya-b2b-full-page[data-widget-mode="popover"]'
    )

    if (someEl) {
      // start listening to changes
      mainDivObserver.observe(someEl)
    }

    // Return a cleanup function to remove the event listeners
    return () => {
      // later, stop listening to changes
      mainDivObserver.disconnect()
    }
  }, [mainDivHeight, executeScroll])

  useEffect(() => {
    const scrollableDiv = document.getElementById('chat-section-scrollable')
    if (!scrollableDiv) return

    const handleScroll = () => {
      const lastAssistantMsgDiv = document.getElementById(`assistant-0`)
      if (lastAssistantMsgDiv) {
        // USER HAS SCROLLED UP
        const lastAssistantMsgDivRect =
          lastAssistantMsgDiv.getBoundingClientRect()
        if (lastAssistantMsgDivRect.top > assistantMsgTop.current) {
          stopScrollDown.current = true
        } else {
          assistantMsgTop.current = lastAssistantMsgDivRect.top
        }
      }
    }

    scrollableDiv.addEventListener('scroll', handleScroll)

    // Cleanup function to remove the event listener
    return () => {
      scrollableDiv.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    // SCROLL TO BOTTOM WHENEVER NEW MESSAGE ADDED
    if (messages?.length > currentMessagesLength) {
      setCurrentMessagesLength(messages?.length)

      // ALLOW AUTO SCROLLING DOWN ON EVERY NEW MESSAGE
      stopScrollDown.current = false
      executeScroll()

      // SET THE TOP OF THE LAST ASSISTANT MESSAGE
      const lastAssistantMsgDiv = document.getElementById(`assistant-0`)
      if (lastAssistantMsgDiv) {
        const lastAssistantMsgDivRect =
          lastAssistantMsgDiv.getBoundingClientRect()
        assistantMsgTop.current = lastAssistantMsgDivRect.top
      }
    }

    // USER HASN'T SCROLLED UP SO SCROLL TO BOTTOM
    if (!stopScrollDown.current) {
      executeScroll()
    }
  }, [messages, currentMessagesLength, executeScroll])

  const onRequestPlanClick = () => {
    Analytics.getInstance().sendEvent('maya_sent_message', {
      message_type: 'cta',
      message_subtype: 'leave_details',
    })

    requestPlan({ slug, token })
    setIsGridMode(false)
  }

  const regenerateMayaResponse = () => {
    let lastUserMessage = ''
    for (let message of messages) {
      if (message?.role === 'user') {
        lastUserMessage = message?.content || ''
      }
    }

    // Close feedback modal when regenerating a response
    if (showFeedbackModal) {
      setShowFeedbackModal(false)
    }

    sendMessageToMaya({ message: lastUserMessage, slug, token })
    Analytics.getInstance().sendEvent('maya_sent_message', {
      message_type: 'user',
    })
    setRegenerateResponse(false)
  }

  const handleQuickReplyClick = (event, reply) => {
    event.preventDefault()

    // Close feedback modal when sending a quick reply
    if (showFeedbackModal) {
      setShowFeedbackModal(false)
    }

    sendMessageToMaya({ message: reply, slug, token })
    Analytics.getInstance().sendEvent('maya_sent_message', {
      message_type: 'quick_reply',
    })
    setPrompt('')

    setTimeout(() => {
      executeScroll()
    }, 100)
  }

  const handleClick = e => {
    if (!isChatReady || isMayaResponding) return

    e.preventDefault()
    if (!prompt) return // If user hasn't typed anything, don't send message

    // Update last active time to restart the feedback modal timer
    updateLastActiveTime()

    // Close feedback modal when sending a new message
    if (showFeedbackModal) {
      setShowFeedbackModal(false)
    }

    sendMessageToMaya({ message: cloneDeep(prompt), slug, token })
    Analytics.getInstance().sendEvent('maya_sent_message', {
      message_type: 'user',
    })
    setPrompt('')

    setTimeout(() => {
      executeScroll()
    }, 100)
  }

  const handleKeyPress = e => {
    if (
      e.keyCode === 13 &&
      !e.shiftKey &&
      !isMayaResponding &&
      isSocketConnectionEstablished
    )
      handleClick(e)
  }

  const updateUserPromp = e => {
    isTyping.current = true
    setPrompt(e.target.value)

    const now = Date.now()
    if (now - lastUpdateTimeCall > 1000) {
      // UPDATE LAST ACTIVE TIME
      // WHEN USER BECOMES IDLE WE SHOW CUSTOMER FEEDBACK MODAL
      updateLastActiveTime()
      lastUpdateTimeCall = now
    }

    // Reset typing flag after a short delay
    setTimeout(() => {
      isTyping.current = false
    }, 500)
  }

  const handleInputFocus = useCallback(e => {
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      e.preventDefault()
      // Force the input to stay in view
      const input = e.target
      const rect = input.getBoundingClientRect()
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const inputTop = rect.top + scrollTop

      // Scroll to keep input in view without animation
      window.scrollTo({
        top: inputTop - 100,
        behavior: 'instant',
      })
    }
  }, [])

  const handleInputBlur = useCallback(e => {
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      e.preventDefault()
      // Prevent the default blur behavior
      setTimeout(() => {
        e.target.focus()
      }, 100)
    }
  }, [])

  const showPoweredByMaya = () => {
    // Find the first element with a non-null showPoweredByMayaButton value
    const poweredByMayaElement = organizationDetails?.configs?.find(
      element => element.showPoweredByMayaButton !== null
    )

    // Get the showPoweredByMayaButton value if found
    const canShowPoweredByMaya = poweredByMayaElement
      ? poweredByMayaElement.showPoweredByMayaButton
      : null

    return canShowPoweredByMaya
  }

  const aIGeneratedQuickReplyOptions = useMemo(() => {
    const lastMessage = messages[messages.length - 1]
    const quickReplyOptions = lastMessage?.quickReplyOptions

    return quickReplyOptions?.length ? quickReplyOptions : null
  }, [messages])

  const containsUserMessage = messages?.some(
    message => message?.type === 'user'
  )

  return (
    <ChatSectionWrapper>
      <ChatSectionScrollable id='chat-section-scrollable'>
        {messages &&
          messages?.map((message, i) => {
            return (
              <>
                {message?.content ? (
                  <MessageComponent
                    language={language}
                    content={message?.content}
                    message={message}
                    disclaimer={message?.disclaimer}
                    sender={message?.role === 'user'}
                    key={`${message?.content} - ${i}`}
                    id={`${message?.role}-${i}`}
                    showFeedbackOption={
                      message?.role === 'assistant' &&
                      message?.type !== 'feedback' &&
                      messages?.length > defaultMessages?.length &&
                      ((i < messages?.length - 1 &&
                        message?.assistantResponseFeedbacks?.length) ||
                        (i === messages?.length - 1 &&
                          isChatReady &&
                          !isMayaResponding))
                    }
                    removeMarginBottom={
                      i === messages?.length - 1 &&
                      (aIGeneratedQuickReplyOptions || showFeedbackModal)
                    }
                  />
                ) : null}
              </>
            )
          })}

        {/* Show feedback modal after all messages if it should be shown */}
        {showFeedbackModal &&
          messages?.length > 0 &&
          messages[messages.length - 1]?.role === 'assistant' && (
            <FeedbackModal
              mobileView={true}
              language={language}
              id='feedback-modal'
            />
          )}

        {/* SHOW LOADER UNTIL MAYA CONTENT STARTS APPEARING */}
        {isMayaResponding && !messages[messages.length - 1].content && (
          <Typing />
        )}
        {regenerateResponse && (
          <RegenerateResponse onClick={regenerateMayaResponse}>
            {getTranslations('regenerate_response')}
          </RegenerateResponse>
        )}

        {/* SHOW QUICK REPLY BUTTONS ONLY INITIALLY */}
        {isChatReady && messages?.length <= defaultMessages?.length ? (
          <MayaQuickRepliesConatiner isHorizontal={isHorizontal}>
            {quickReplies &&
              quickReplies?.map(reply => (
                <MayaQuickRepliesBullet
                  key={reply?.content || ``}
                  onClick={e => {
                    handleQuickReplyClick(e, reply?.content || ``)
                    Analytics.getInstance().sendEvent('maya_click_button', {
                      button_type: 'quick_reply',
                      button_text: reply?.content || '',
                    })
                  }}
                >
                  {reply?.content || ``}
                </MayaQuickRepliesBullet>
              ))}
          </MayaQuickRepliesConatiner>
        ) : null}

        {/* SHOW AI GENERATED QUICK REPLY OPTIONS */}
        {aIGeneratedQuickReplyOptions ? (
          <MayaQuickRepliesConatiner
            id='maya-ai-quick-replies-container'
            isHorizontal={isHorizontal}
          >
            {aIGeneratedQuickReplyOptions &&
              aIGeneratedQuickReplyOptions?.map(reply => (
                <MayaQuickRepliesBullet
                  key={reply || ``}
                  onClick={e => {
                    handleQuickReplyClick(e, reply || ``)
                    Analytics.getInstance().sendEvent('maya_click_button', {
                      button_type: 'quick_reply_ai',
                      button_text: reply || '',
                    })
                  }}
                >
                  {reply || ``}
                </MayaQuickRepliesBullet>
              ))}
          </MayaQuickRepliesConatiner>
        ) : null}

        <div
          id='maya-chat-scrollable-extra-padding'
          style={{
            paddingTop: !isLessThanLaptop
              ? '160px'
              : isHorizontal
              ? '150px'
              : `${
                  process.env.REACT_APP_API_ENV === 'production' ? 230 : 260
                }px`,
          }}
        ></div>
      </ChatSectionScrollable>
      {!readOnlyMode ? (
        <MayaInputBox id='maya-input-box'>
          <MayaInputContainerWrapper id='maya-input-container-wrapper'>
            <MayaInputContainer>
              {!(
                // WHEN HORIZONTAL VIEW ON MOBILE AND QUICK REPLIES ARE SHOWING
                // DONT SHOW THESE CTAS DUE TO LOW SPACE
                (
                  isLessThanLaptop &&
                  isHorizontal &&
                  messages?.length <= defaultMessages?.length
                )
              ) &&
                isLessThanLaptop &&
                intent &&
                grids && (
                  <ChatSectionCtaWrapper
                    id='chat-section-cta-wrapper'
                    isHorizontal={isHorizontal}
                    isLessThanLaptop={isLessThanLaptop}
                  >
                    {containsUserMessage ? (
                      <ChatSectionCta
                        onClick={() => {
                          Analytics.getInstance().sendEvent(
                            'maya_click_button',
                            {
                              button_type: 'cta',
                              button_text: `${getTranslations(
                                'see_recommendations_cta_title'
                              )}`,
                            }
                          )
                          setTravelPlanView()
                        }}
                      >
                        {getTranslations('see_recommendations_cta_title')}
                      </ChatSectionCta>
                    ) : null}
                    {organizationDetails?.configs?.length &&
                    organizationDetails?.configs[0]
                      .enableRequestPlanCtaFullPageMobile ? (
                      organizationDetails?.configs[0].requestPlanUrl ? (
                        <ChatSectionCtaLink
                          href={organizationDetails.configs[0].requestPlanUrl}
                          onClick={() => {
                            Analytics.getInstance().sendEvent(
                              'maya_click_button',
                              {
                                button_type: 'cta',
                                button_text: `${getTranslations(
                                  'request_plan_cta'
                                )}`,
                                button_subtype: 'leave_details',
                              }
                            )
                          }}
                        >
                          {getTranslations('request_plan_cta')}
                        </ChatSectionCtaLink>
                      ) : (
                        <ChatSectionCta
                          onClick={() => {
                            Analytics.getInstance().sendEvent(
                              'maya_click_button',
                              {
                                button_type: 'cta',
                                button_text: `${getTranslations(
                                  'request_plan_cta'
                                )}`,
                                button_subtype: 'leave_details',
                              }
                            )
                            onRequestPlanClick()
                          }}
                          disabled={
                            isPlanRequested || isMayaResponding || !isChatReady
                          }
                        >
                          {!isPlanRequested
                            ? getTranslations('request_plan_cta')
                            : getTranslations('is_plan_requested')}
                        </ChatSectionCta>
                      )
                    ) : null}
                  </ChatSectionCtaWrapper>
                )}

              <MayaMessageInput
                isHorizontal={isHorizontal}
                type='text'
                placeholder={getTranslations('chat_placeholder')}
                value={prompt}
                onChange={updateUserPromp}
                onKeyDown={handleKeyPress}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                id='maya-message-input'
              />
              <MayaMessageInputSubmitButton
                isHorizontal={isHorizontal}
                onClick={handleClick}
                disabled={isMayaResponding || !isChatReady}
                $isMayaResponding={isMayaResponding || !isChatReady}
              >
                {isMayaResponding ||
                !isSocketConnectionEstablished ||
                !isChatReady ? (
                  <TypingContainerInInput>
                    <TypingDot />
                    <TypingDot />
                  </TypingContainerInInput>
                ) : (
                  <Image
                    src={SubmitText}
                    alt='message send button img'
                    width={isLessThanLaptop && isHorizontal ? '35px' : '40px'}
                    height={isLessThanLaptop && isHorizontal ? '35px' : '40px'}
                    minWidth={
                      isLessThanLaptop && isHorizontal ? '35px' : '40px'
                    }
                    minHeight={
                      isLessThanLaptop && isHorizontal ? '35px' : '40px'
                    }
                    radius={'0px'}
                  />
                )}
              </MayaMessageInputSubmitButton>
            </MayaInputContainer>
            {isLessThanLaptop && showPoweredByMaya() ? (
              <PoweredByMayaWrapper>
                <PoweredByMayaText> Powered by</PoweredByMayaText>{' '}
                <PoweredByMayaLink
                  href={'https://www.mayatravel.ai/'}
                  target={'_blank'}
                >
                  Maya
                </PoweredByMayaLink>
              </PoweredByMayaWrapper>
            ) : null}
            {!isLessThanLaptop || !isHorizontal ? (
              <MayaEmptyMarginContainer />
            ) : null}
          </MayaInputContainerWrapper>
        </MayaInputBox>
      ) : (
        <ReadOnlyDisclaimerWrapper>Read Only Mode</ReadOnlyDisclaimerWrapper>
      )}
    </ChatSectionWrapper>
  )
}

const mapStateToProps = state => {
  return {
    isPlanRequested: state?.maya?.isPlanRequested,
    readOnlyMode: state?.maya?.readOnlyMode,
    slug: state?.maya?.slug,
    token: state?.maya?.token,
    messages: state?.maya?.messages,
    isMayaResponding: state?.maya?.isMayaResponding,
    isChatReady: state?.maya?.isChatReady,
    regenerateResponse: state?.maya?.regenerateResponse,
    quickReplies: state?.maya?.quickReplies,
    defaultMessages: state?.maya?.defaultMessages,
    intent: state?.maya?.intent,
    grids: getGrids(state),
    organizationSlug: state?.general?.organizationSlug,
    isSocketConnectionEstablished: state?.maya?.isSocketConnectionEstablished,
    organizationDetails: state?.maya?.organizationDetails,
    showFeedbackModal: state?.maya?.showFeedbackModal,
  }
}

const mapDispatchToProps = dispatch => ({
  setRegenerateResponse: data => dispatch(setRegenerateResponse(data)),
  setIsGridMode: data => dispatch(setIsGridMode(data)),
  updateLastActiveTime: () => dispatch(updateLastActiveTime()),
  setShowFeedbackModal: data => dispatch(setShowFeedbackModal(data)),
})

export default memo(
  connect(mapStateToProps, mapDispatchToProps)(ChatSection),
  arePropsEqual
)
